import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getMinecraftResources, toggleLike } from '../../firebase/minecraft';
import { MinecraftResource } from '../../types/minecraft';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import SearchIcon from '../../components/icons/SearchIcon';
import PlusIcon from '../../components/icons/PlusIcon';

const MinecraftWorldShare: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [resources, setResources] = useState<MinecraftResource[]>([]);
  const [filteredResources, setFilteredResources] = useState<MinecraftResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const data = await getMinecraftResources('world');
        setResources(data);
        setFilteredResources(data);
      } catch (error) {
        console.error('리소스 로드 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredResources(resources);
      return;
    }

    const filtered = resources.filter(resource => 
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredResources(filtered);
  }, [searchTerm, resources]);

  const handleLike = async (resource: MinecraftResource) => {
    if (!currentUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      await toggleLike(resource.id, currentUser.uid, resource.likes, resource.likedBy);
      
      const updatedResources = resources.map(r => {
        if (r.id === resource.id) {
          const isLiked = r.likedBy.includes(currentUser.uid);
          return {
            ...r,
            likes: isLiked ? r.likes - 1 : r.likes + 1,
            likedBy: isLiked 
              ? r.likedBy.filter(id => id !== currentUser.uid)
              : [...r.likedBy, currentUser.uid]
          };
        }
        return r;
      });
      setResources(updatedResources);
      setFilteredResources(updatedResources);
    } catch (error) {
      console.error('좋아요 처리 오류:', error);
      alert('좋아요 처리 중 오류가 발생했습니다.');
    }
  };

  const handleDownload = async (resource: MinecraftResource) => {
    try {
      window.open(resource.fileUrl, '_blank');
      const { incrementDownloadCount } = await import('../../firebase/minecraft');
      await incrementDownloadCount(resource.id, resource.downloadCount);
      
      const updatedResources = resources.map(r => {
        if (r.id === resource.id) {
          return { ...r, downloadCount: r.downloadCount + 1 };
        }
        return r;
      });
      setResources(updatedResources);
      setFilteredResources(updatedResources);
    } catch (error) {
      console.error('다운로드 오류:', error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
    }}>
      {/* 헤더 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        alignSelf: 'stretch',
        padding: '8px 0px 32px 0px'
      }}>
        <h1 style={{ 
          color: 'var(--Color-text-normal, #161616)',
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: 'var(--Font-Font-size-Heading-2, 32px)',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '150%',
          letterSpacing: '-0.64px',
          margin: 0
        }}>
          🗺️ 월드 공유
        </h1>
      </div>

      {/* 검색 */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          position: 'relative',
          width: '100%'
        }}>
          <input
            type="text"
            placeholder="제목, 설명, 태그로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              height: '48px',
              paddingLeft: '44px',
              paddingRight: '16px',
              background: 'var(--Color-fill-white, white)',
              borderRadius: '8px',
              border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
              fontSize: '13px',
              fontFamily: 'Pretendard',
              fontWeight: '400',
              lineHeight: '19.5px',
              color: searchTerm ? '#161616' : 'var(--Color-text-assistive, #989898)',
              outline: 'none'
            }}
          />
          <div style={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SearchIcon width={20} height={20} />
          </div>
        </div>
      </div>

      {/* 업로드 버튼 */}
      {currentUser && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '24px'
        }}>
          <Button
            width="120px"
            height="40px"
            variant="submit"
            onClick={() => navigate('/minecraft-education/upload')}
            leftIcon={<PlusIcon width={14} height={14} fill="white" />}
          >
            업로드
          </Button>
        </div>
      )}

      {/* 리소스 목록 */}
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '80px 0',
          color: 'var(--Color-text-assistive, #989898)',
          fontFamily: 'Pretendard',
          fontSize: '14px'
        }}>
          로딩 중...
        </div>
      ) : filteredResources.length === 0 ? (
        <Card
          width="100%"
          padding="64px 24px"
          alignItems="center"
          gap={10}
        >
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontFamily: 'Pretendard',
            fontSize: '16px',
            fontWeight: '400',
            lineHeight: '24px',
            textAlign: 'center'
          }}>
            {searchTerm ? '검색 결과가 없습니다.' : '업로드된 월드가 없습니다.'}
          </div>
        </Card>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '24px'
        }}>
          {filteredResources.map((resource) => (
            <Card
              key={resource.id}
              width="100%"
              padding="0"
              alignItems="stretch"
              gap={0}
              style={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onClick={() => navigate(`/minecraft-education/${resource.id}`)}
            >
              {/* 썸네일 */}
              <div style={{
                width: '100%',
                height: '200px',
                background: '#F0F0F0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {resource.thumbnailUrl ? (
                  <img
                    src={resource.thumbnailUrl}
                    alt={resource.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    fontSize: '64px'
                  }}>
                    🗺️
                  </div>
                )}
              </div>

              {/* 콘텐츠 */}
              <div style={{
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                flex: 1
              }}>
                {/* 제목 */}
                <h3 style={{
                  margin: 0,
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'Pretendard',
                  fontSize: '16px',
                  fontWeight: '600',
                  lineHeight: '24px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {resource.title}
                </h3>

                {/* 설명 */}
                <p style={{
                  margin: 0,
                  color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                  fontFamily: 'Pretendard',
                  fontSize: '13px',
                  fontWeight: '400',
                  lineHeight: '19.5px',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {resource.description}
                </p>

                {/* 태그 */}
                {resource.tags.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px'
                  }}>
                    {resource.tags.slice(0, 3).map((tag, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          border: '1px solid var(--Color-line-normal, #F0F0F0)',
                          color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400'
                        }}
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                )}

                {/* 메타 정보 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 'auto'
                }}>
                  <div style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontFamily: 'Pretendard',
                    fontSize: '12px',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    {resource.authorName} • {formatFileSize(resource.fileSize)}
                  </div>
                  <div style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontFamily: 'Pretendard',
                    fontSize: '12px',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    다운로드 {resource.downloadCount}
                  </div>
                </div>
              </div>

              {/* 액션 버튼 */}
              <div style={{
                padding: '12px 16px',
                display: 'flex',
                gap: '8px',
                borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
                alignItems: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => handleDownload(resource)}
                  style={{
                    padding: '8px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="다운로드"
                >
                  <span style={{ fontSize: '20px' }}>📥</span>
                </button>
                <button
                  onClick={() => handleLike(resource)}
                  style={{
                    padding: '8px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: resource.likedBy.includes(currentUser?.uid || '') ? '#FF3838' : '#989898'
                  }}
                  title={resource.likedBy.includes(currentUser?.uid || '') ? '좋아요 취소' : '좋아요'}
                >
                  <span style={{ fontSize: '20px' }}>❤️</span>
                </button>
                <div style={{ flex: 1 }} />
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontFamily: 'Pretendard',
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '18px'
                }}>
                  좋아요 {resource.likes}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MinecraftWorldShare;


